import { MailPlus, Plus, Search, ShieldCheck, UserCog } from 'lucide-react';
import { Avatar } from '../components/Avatar';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { Role, RoleDef, User } from '../api/types';
import { useAuth } from '../auth';
import { Topbar } from '../components/Layout';
import { Empty, Field, Modal, PageLoader, useToast } from '../components/ui';
import { formatDate } from '../format';
import { useI18n } from '../i18n';
import { roleDescription, roleLabel } from '../roles';

const roleTone: Record<string, string> = { admin: 'orange', creator: 'green', analyst: 'blue', enumerator: 'blue', respondent: '' };

/** User and role administration (FR-15). */
export function UsersPage() {
  const { user: me, can } = useAuth();
  const toast = useToast();
  const { t } = useI18n();
  const [users, setUsers] = useState<User[] | null>(null);
  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.listUsers(query || undefined).then((r) => setUsers(r.users), (e) => toast(e instanceof ApiError ? e.message : t('users.load_failed'), 'error'));
  }, [query, toast, t]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  // Roles are configurable; reload them with the user list so counts stay current.
  useEffect(() => {
    api.roles().then((r) => setRoles(r.roles), (e) => toast(e instanceof ApiError ? e.message : t('roles.load_failed'), 'error'));
  }, [users, toast, t]);

  if (!can('users.manage')) return <Navigate to="/" replace />;

  async function update(u: User, patch: Partial<{ role: Role; active: boolean }>) {
    try {
      const updated = await api.updateUser(u.id, patch);
      setUsers((list) => list?.map((x) => (x.id === u.id ? updated : x)) ?? null);
      toast(t('users.updated'));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('users.update_failed'), 'error');
    }
  }

  const nameOf = (key: Role) => roleLabel(t, key, roles.find((r) => r.key === key)?.name);

  return (
    <>
      <Topbar title={t('users.title')}>
        <Link className="btn" to="/admin/invitations">
          <MailPlus /> {t('invitations.title')}
        </Link>
        <button className="btn primary" onClick={() => setCreating(true)}>
          <Plus /> {t('users.add')}
        </button>
      </Topbar>

      <div className="card kpis mb-16">
        {roles.map((r) => (
          <div key={r.key} className="kpi" title={roleDescription(t, r)}>
            <div className="tile">{r.key === 'admin' ? <ShieldCheck /> : <UserCog />}</div>
            <div className="body">
              <div className="label">{nameOf(r.key)}</div>
              <div className="value">{r.userCount}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-head">
          <h2 className="grow">{t('users.accounts')}</h2>
          <div className="search" style={{ width: 260, maxWidth: '100%' }}>
            <Search />
            <input className="input" placeholder={t('users.search_placeholder')} value={query} onChange={(e) => setQuery(e.target.value)} aria-label={t('users.search_label')} />
          </div>
        </div>
        {users === null ? (
          <PageLoader />
        ) : users.length === 0 ? (
          <Empty icon={<UserCog />} title={t('users.none')} />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('users.col_user')}</th>
                  <th>{t('users.col_role')}</th>
                  <th>{t('users.col_status')}</th>
                  <th>{t('users.col_created')}</th>
                  <th aria-label={t('surveys.col_actions')} />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="row gap-12">
                        <Avatar user={u} size={30} />
                        <div>
                          <div className="title-cell">{u.name || '—'}</div>
                          <div className="muted">{u.title ? `${u.title} · ${u.email}` : u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <select
                        className="select"
                        style={{ height: 32, width: 150 }}
                        value={u.role}
                        aria-label={t('users.role_for', { email: u.email })}
                        disabled={u.id === me?.id}
                        title={u.id === me?.id ? t('users.cant_change_own') : undefined}
                        onChange={(e) => void update(u, { role: e.target.value as Role })}
                      >
                        {roles.map((r) => (
                          <option key={r.key} value={r.key}>
                            {nameOf(r.key)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {u.pending ? (
                        <span className="pill dot orange">{t('users.pending')}</span>
                      ) : (
                        <span className={`pill dot ${u.active ? roleTone.creator : 'red'}`}>{u.active ? t('users.active') : t('users.disabled')}</span>
                      )}
                    </td>
                    <td className="muted">{formatDate(u.createdAt)}</td>
                    <td className="num">
                      {u.id !== me?.id && !u.pending && (
                        <button className={`btn sm ${u.active ? 'danger' : ''}`} onClick={() => void update(u, { active: !u.active })}>
                          {u.active ? t('users.disable') : t('users.enable')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid cols-2 mt-16">
        {roles.map((r) => (
          <div key={r.key} className="card card-pad row gap-12">
            <span className={`pill ${roleTone[r.key] ?? ''}`}>{nameOf(r.key)}</span>
            <span className="small muted">{roleDescription(t, r)}</span>
          </div>
        ))}
      </div>
      {me?.role === 'admin' && (
        <p className="mt-16">
          <Link to="/admin/roles">{t('users.manage_roles')}</Link>
        </p>
      )}

      {creating && (
        <CreateUser
          roles={roles}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
            toast(t('users.created'));
          }}
        />
      )}
    </>
  );
}

function CreateUser({ roles, onClose, onCreated }: { roles: RoleDef[]; onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'creator' as Role });
  const selected = roles.find((r) => r.key === form.role);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFields({});
    setError('');
    try {
      await api.createUser(form);
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        setFields(err.fields);
        if (!Object.keys(err.fields).length) setError(err.message);
      }
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('users.add')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" form="create-user" disabled={busy}>
            {busy ? t('users.creating') : t('users.create')}
          </button>
        </>
      }
    >
      <form id="create-user" className="form-grid" onSubmit={submit}>
        {error && <div className="banner error">{error}</div>}
        <Field label={t('auth.name')}>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={t('auth.email')} error={fields.email}>
          <input className="input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label={t('users.temp_password')} error={fields.password} hint={t('users.temp_password_hint')}>
          <input className="input" type="text" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
        <Field label={t('users.role')} error={fields.role} hint={selected ? roleDescription(t, selected) : undefined}>
          <select className="select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
            {roles.map((r) => (
              <option key={r.key} value={r.key}>
                {roleLabel(t, r.key, r.name)}
              </option>
            ))}
          </select>
        </Field>
      </form>
    </Modal>
  );
}
